import {type FC, useState} from 'react';
import DeletingConfirmationDialog from '@/components/PointCloudsUploadForm/common/DeletingConfirmationDialog';
import PointCloudReuploadDialog from '@/components/PointCloudsUploadForm/common/PointCloudReuploadDialog';
import PointCloudsAddInfoButton from '@/components/PointCloudsUploadForm/common/PointCloudsAddInfoButton';
import SlamPointCloudInfo from '@/components/PointCloudsUploadForm/slam/SlamPointCloudInfo';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import type {SlamPointCloud, SlamPointCloudCreate} from '@/domain/entities/SlamPointCloud';
import SlamPointCloudUploadingDialog from './SlamPointCloudUploadingDialog';

type Props = {
  slamPointCloud: SlamPointCloud | undefined;
  lidarTechnologies: LidarTechnology[];
  canUpload: boolean;
  canDelete: boolean;
  onDelete: (pointCloudUuid: string) => void;
  onDownload: (pointCloudUuid: string) => void;
  onUpload: (pointCloud: Partial<SlamPointCloudCreate>, file: File) => void;
  onReupload: (pointCloudUuid: string, file: File) => void;
};

const SlamPointCloudButton: FC<Props> = ({
  slamPointCloud,
  lidarTechnologies,
  canUpload,
  canDelete,
  onDelete,
  onUpload,
  onDownload,
  onReupload,
}) => {
  const [isUploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [isInfoDialogOpen, setInfoDialogOpen] = useState<boolean>(false);
  const [isReuploadDialogOpen, setReuploadDialogOpen] = useState<boolean>(false);

  const handleDelete = () => slamPointCloud && onDelete(slamPointCloud?.uuid);
  const handleDownload = () => slamPointCloud && onDownload(slamPointCloud?.uuid);
  const handleUpload = (pointCloud: Partial<SlamPointCloudCreate>, file: File) => {
    onUpload(pointCloud, file);
    setUploadDialogOpen(false);
  };
  const handleReupload = (file: File) => {
    setReuploadDialogOpen(false);
    slamPointCloud && onReupload(slamPointCloud?.uuid, file);
  };

  return (
    <>
      <PointCloudsAddInfoButton
        canUpload={canUpload}
        pointCloud={slamPointCloud}
        onInfoClick={() => setInfoDialogOpen(true)}
        onUploadClick={() => setUploadDialogOpen(true)}
      />
      {isInfoDialogOpen && slamPointCloud && (
        <SlamPointCloudInfo
          canDelete={canDelete}
          canUpload={canUpload}
          slamPointCloud={slamPointCloud}
          lidarTechnologies={lidarTechnologies}
          onClose={() => setInfoDialogOpen(false)}
          onDelete={() => setDeleteDialogOpen(true)}
          onDownload={handleDownload}
          onReupload={() => setReuploadDialogOpen(true)}
        />
      )}
      {isDeleteDialogOpen && (
        <DeletingConfirmationDialog onClose={() => setDeleteDialogOpen(false)} onDelete={handleDelete} />
      )}
      {isUploadDialogOpen && (
        <SlamPointCloudUploadingDialog
          lidarTechnologies={lidarTechnologies}
          onUploadPointCloud={handleUpload}
          onClose={() => setUploadDialogOpen(false)}
        />
      )}
      {isReuploadDialogOpen && (
        <PointCloudReuploadDialog onReupload={handleReupload} onClose={() => setReuploadDialogOpen(false)} />
      )}
    </>
  );
};

export default SlamPointCloudButton;
